import { trackMetaEvent } from '../utils/metaPixel';
import SEO from '../components/SEO';
import PlatformRotator from '../components/PlatformRotator';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { EnvelopeSimple, Lock, Buildings, ArrowLeft, X } from '@phosphor-icons/react';
import { Paper, TextInput, PasswordInput, Button, Divider, Text, Title, Anchor } from '@mantine/core';
import GoogleSignInButton from '../components/GoogleSignInButton';
import Logo from '../components/Logo';
import Turnstile from '../components/Turnstile';

export default function RegisterPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cfToken, setCfToken] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(email, password, companyName, cfToken);
      trackMetaEvent('CompleteRegistration');
      navigate('/app');
    } catch (err) {
      setError(err.response?.data?.email?.[0] || t('auth.register.error'));
    } finally {
      setLoading(false);
    }
  };

  const labelStyles = {
    label: { fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-5)', marginBottom: 4 },
  };

  return (
    <>
    <SEO title={t('auth.register.seoTitle')} description={t('auth.register.seoDescription')} path="/rejestracja" />
    <div className="min-h-screen bg-[#F6F5F4] flex">

      {/* Left: form */}
      <div className="flex-1 flex items-center justify-center px-4 py-3">
        <div className="w-full max-w-md">
          <div className="text-center mb-4">
            <Link to="/" className="inline-flex items-center mb-3">
              <Logo className="h-10" />
            </Link>
            <Title order={2} className="text-gray-900">
              {t('auth.register.title')}
            </Title>
            <Text size="sm" c="dimmed" mt={2}>
              {t('auth.register.subtitle')} <PlatformRotator />
            </Text>
          </div>

          <Paper shadow="xs" p="md" radius="md" withBorder>
            <form onSubmit={handleSubmit} className="space-y-2.5">
              {error && (
                <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg bg-red-50 border border-red-200">
                  <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                    <X size={12} weight="bold" className="text-red-500" />
                  </div>
                  <Text size="xs" c="red.7" fw={500} lh={1.4}>{error}</Text>
                </div>
              )}

              <TextInput
                label={t('auth.register.companyLabel')}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                leftSection={<Buildings size={16} />}
                placeholder={t('auth.register.companyPlaceholder')}
                size="sm"
                styles={labelStyles}
              />

              <TextInput
                label={t('auth.email')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                leftSection={<EnvelopeSimple size={16} />}
                placeholder={t('auth.emailPlaceholder')}
                required
                size="sm"
                styles={labelStyles}
              />

              <PasswordInput
                label={t('auth.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                leftSection={<Lock size={16} />}
                placeholder={t('auth.passwordMin8')}
                required
                minLength={8}
                size="sm"
                styles={labelStyles}
              />

              <Turnstile onToken={setCfToken} />

              <Button
                type="submit"
                fullWidth
                color="brand"
                loading={loading}
                styles={{
                  root: {
                    fontWeight: 700,
                    fontSize: 15,
                    height: 42,
                    transition: 'box-shadow 0.15s',
                    '&:hover': { boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
                  },
                }}
              >
                {t('auth.register.submit')}
              </Button>
            </form>

            <Divider label={t('auth.or')} labelPosition="center" my="sm" styles={{ label: { color: 'var(--mantine-color-gray-4)', fontSize: '0.75rem' } }} />

            <GoogleSignInButton />

            <Text ta="center" c="dimmed" size="xs" mt="sm">
              {t('auth.register.haveAccount')}{' '}
              <Anchor component={Link} to="/logowanie" size="xs" fw={600} c="brand">
                {t('auth.logIn')}
              </Anchor>
            </Text>
          </Paper>

          <Text ta="center" mt="sm">
            <Anchor component={Link} to="/" size="xs" c="dimmed" className="inline-flex items-center gap-1">
              <ArrowLeft size={12} /> {t('auth.backHome')}
            </Anchor>
          </Text>
        </div>
      </div>

      {/* Right: image panel (hidden on mobile) */}
      <div className="hidden lg:block lg:w-[55%] relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&q=80&auto=format"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/80 via-indigo-800/70 to-indigo-900/80" />

        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="max-w-md">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                <img src="/logo_icon_only.svg" alt="" className="w-5 h-5 brightness-0 invert" />
              </div>
              <span className="text-white/80 text-sm font-medium">Droid.pl</span>
            </div>
            <h2 className="text-3xl font-bold text-white leading-tight mb-3">
              {t('auth.register.heroTitle')}
            </h2>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              {t('auth.register.heroText')}
            </p>
            <div className="flex gap-6">
              <div>
                <p className="text-2xl font-bold text-white">{t('auth.register.stat1Value')}</p>
                <p className="text-white/50 text-xs">{t('auth.register.stat1Label')}</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{t('auth.register.stat2Value')}</p>
                <p className="text-white/50 text-xs">{t('auth.register.stat2Label')}</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{t('auth.register.stat3Value')}</p>
                <p className="text-white/50 text-xs">{t('auth.register.stat3Label')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
    </>
  );
}
